import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

const urlOptions: QueryUrlOptions = {
  identifier: { type: 'commands' },
  op: 'query',
  resourcePath: 'api/v2/phone-capture-prompt',
};

/**
 * How long a dismissal hides the overlay and the dashboard card, which share one snooze.
 * `soft` lasts until the next login; `long` until login 4, then 10, or two weeks,
 * whichever comes first.
 */
export type PhoneCapturePromptSnoozeKind = 'soft' | 'long';

/** What the user payload reports about this login's eligibility. */
export interface PhoneCapturePromptEligibility {
  eligible: boolean;
  /** Why the prompt is suppressed; null when eligible. */
  reason: string | null;
}

/**
 * Records a dismissal with POST `/api/v2/phone-capture-prompt/snooze`, skipping the
 * request cache.
 *
 * Advances the server-side cadence, so it must be awaited before the prompt is torn
 * down — a dismissal followed by an immediate reload would otherwise re-show it.
 *
 * @param platform attributed onto the dismissal funnel event; omitted sends nothing.
 * @param surface the prompt that was dismissed, for the funnel only; omitted means the overlay.
 */
export function snoozePhoneCapturePromptRequest(
  kind: PhoneCapturePromptSnoozeKind,
  platform?: string,
  surface?: string
): RequestInfo {
  const url = buildBaseURL(urlOptions);
  return {
    url: `${url}/snooze`,
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    body: JSON.stringify({ kind, ...(platform && { platform }), ...(surface && { surface }) }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Reports that the prompt actually rendered, with POST
 * `/api/v2/phone-capture-prompt/shown`, skipping the request cache.
 *
 * This — not reading eligibility — is what advances the cadence, so it must be sent
 * once per render and never from a read path.
 */
export function phoneCapturePromptShownRequest(platform?: string): RequestInfo {
  const url = buildBaseURL(urlOptions);
  return {
    url: `${url}/shown`,
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    body: JSON.stringify(platform ? { platform } : {}),
    cacheOptions: { [SkipCache]: true },
  };
}
